import { useRef, useEffect } from "react";

interface RecordingVisualizerProps {
  analyserNode: AnalyserNode | null;
  isPaused: boolean;
  height?: number;
}

const WAVE_COLOR = "#ef4444";
const PAUSED_TINT = "rgba(239, 68, 68, 0.25)";

export default function RecordingVisualizer({
  analyserNode,
  isPaused,
  height = 100,
}: RecordingVisualizerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const historyRef = useRef<number[]>([]);
  const animFrameRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !analyserNode) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Theme-aware neutral background (canvas colors can't use CSS var() strings)
    const bgColor = `oklch(${getComputedStyle(document.documentElement).getPropertyValue("--b2")})`;

    const bufferLength = analyserNode.fftSize;
    const dataArray = new Uint8Array(bufferLength);
    const maxHistory = Math.ceil(canvas.width / 3);

    const draw = () => {
      animFrameRef.current = requestAnimationFrame(draw);

      if (!isPaused) {
        analyserNode.getByteTimeDomainData(dataArray);
        let sum = 0;
        for (let i = 0; i < bufferLength; i++) {
          const v = (dataArray[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / bufferLength);
        const amplitude = Math.min(rms * 4, 1);
        historyRef.current.push(amplitude);
        if (historyRef.current.length > maxHistory) {
          historyRef.current = historyRef.current.slice(-maxHistory);
        }
      }

      const w = canvas.width;
      const h = canvas.height;
      const history = historyRef.current;

      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, w, h);

      const barWidth = 2;
      const gap = 1;
      const step = barWidth + gap;
      const centerY = h / 2;

      ctx.fillStyle = isPaused ? PAUSED_TINT : WAVE_COLOR;

      if (!isPaused) {
        ctx.shadowColor = WAVE_COLOR;
        ctx.shadowBlur = 8;
      }

      for (let i = 0; i < history.length; i++) {
        const x = w - (history.length - i) * step;
        if (x < 0) continue;
        const barHeight = Math.max(2, history[i] * (h * 0.8));
        const y = centerY - barHeight / 2;

        ctx.beginPath();
        ctx.roundRect(x, y, barWidth, barHeight, 1);
        ctx.fill();
      }

      ctx.shadowBlur = 0;

      // Center line
      ctx.strokeStyle = "rgba(239, 68, 68, 0.12)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, centerY);
      ctx.lineTo(w, centerY);
      ctx.stroke();
    };

    draw();

    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [analyserNode, isPaused]);

  return (
    <canvas
      ref={canvasRef}
      width={700}
      height={height}
      className="w-full block"
      style={{ height: `${height}px` }}
    />
  );
}
